import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Banner, Button, Card, Container, Pictogram, Stack, StackItem, Tag, Typography } from '@optimus-web/core';
import { LocalizedText, SentryErrorBoundary } from '@mews/framework';
import { SentryTeams } from '@mews/enums';
import { TextKey, ArticlePath, createGuideLink } from '@mews-commander/enums';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { NewTabLink } from '@mews-commander/ui';
import { TagWithStatus } from '../TagWithStatus';
import { usePasskeyAdd } from './usePasskeyAdd';
import { PasskeyList } from './PasskeyList';

const PASSKEY_GUIDELINK_URL = createGuideLink(ArticlePath.CreatePasskey);

export const Passkeys = () => {
    const { data: registeredKeys } = useRepositoryQuery('users', 'getRegisteredCredentials', {});
    const hasPasskey = !!registeredKeys?.passkeys.length;
    const hasPasskeySupport = browserSupportsWebAuthn();

    const { handlePasskeyAdd, isPending } = usePasskeyAdd();

    return (
        <SentryErrorBoundary team={SentryTeams.Users}>
            <Card>
                <Container padding={['300', '300', hasPasskey ? '200' : '0', '300']}>
                    <Stack vertical spacing="200">
                        <Pictogram value="passkey" size="large" />
                        <Stack spacing="100" itemAlignment="center" distribution="space-between">
                            <Stack spacing="100" itemAlignment="center">
                                <Typography textStyle="titleSmallStrong" as="div">
                                    <LocalizedText textKey={TextKey.SettingsSecurityPasskeysCardTitle} />
                                </Typography>
                                <Tag variant="primary">
                                    <LocalizedText textKey={TextKey.Recommended} />
                                </Tag>
                            </Stack>
                            <TagWithStatus isEnabled={hasPasskey} />
                        </Stack>
                        <Typography textStyle="bodyMedium" as="div">
                            <LocalizedText textKey={TextKey.SettingsSecurityPasskeysCardDescription} />{' '}
                            <NewTabLink url={PASSKEY_GUIDELINK_URL} variant="default" inherit={false}>
                                <LocalizedText textKey={TextKey.SettingsSecurityPasskeysCardLink} />
                            </NewTabLink>
                        </Typography>
                    </Stack>
                </Container>

                {hasPasskey && <PasskeyList passkeys={registeredKeys.passkeys} />}

                <Container padding={['200', '300', '300', '300']}>
                    {hasPasskeySupport ? (
                        <StackItem>
                            <Button
                                type="submit"
                                variant={hasPasskey ? 'secondary' : 'primary'}
                                size="small"
                                onClick={handlePasskeyAdd}
                                loading={isPending}
                            >
                                <LocalizedText textKey={TextKey.SettingsSecurityPasskeysCardButton} />
                            </Button>
                        </StackItem>
                    ) : (
                        <Banner
                            variant="warning"
                            primaryText={
                                <LocalizedText textKey={TextKey.SettingsSecurityPasskeysUnsupportedBannerTitle} />
                            }
                            secondaryText={TextKey.SettingsSecurityPasskeysUnsupportedBannerDescription}
                        />
                    )}
                </Container>
            </Card>
        </SentryErrorBoundary>
    );
};
